import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { FigmaPluginApi, ZeytalTokens } from "@zeytal/types";

import { toKebabCase } from "@endgame/utils/toKebabCase";

import { variableAliasKeyword, varNameStartKeyword } from "../utils/tokens.ts";

const flattenVariables = (
  figmaVariablesExportsStrings: string[],
  userConfig: ZeytalCLIPureConfig,
) => {
  const flatVarsStructure: ZeytalTokens.FlatVarsStructure = {};

  for (const varsExportString of figmaVariablesExportsStrings) {
    if (!varsExportString) continue;

    const varsExport: ZeytalTokens.FigmaVariableCollections =
      JSON.parse(varsExportString);

    varsExport.forEach((collection) => {
      const { name: collectionName, modes } = collection;

      if (
        !!userConfig.variables.collectionsIgnorePrefix &&
        typeof userConfig.variables.collectionsIgnorePrefix === "string" &&
        collectionName.startsWith(userConfig.variables.collectionsIgnorePrefix)
      ) {
        return;
      }

      modes.forEach((mode) => {
        const { name: modeName, variables } = mode;

        variables.forEach((variable) => {
          const {
            id: varId,
            name: variableName,
            resolvedType,
            value,
          } = variable;

          if (!value) {
            return;
          }

          const varNameCollection = `${
            collectionName.replace(/^[^a-zA-Z]+/, "") // No starting digit or special chars in collection names
          }-`;
          const varNameMode = modes.length > 1 ? `-${modeName}` : "";
          const varNameOriginal = variableName;

          const structuredVariableName = toKebabCase(
            `${varNameStartKeyword}${varNameCollection}${varNameOriginal}${varNameMode}`,
          );

          if (
            typeof value === "string" ||
            typeof value === "boolean" ||
            typeof value === "number"
          ) {
            // Is a string, boolean or number variable
            flatVarsStructure[structuredVariableName] = {
              id: varId,
              varType: resolvedType,
              modeName: modes.length > 1 ? modeName : undefined,
              value,
            };
          } else if (
            typeof value === "object" &&
            value !== null &&
            Object.hasOwn(value, "r") &&
            Object.hasOwn(value, "g") &&
            Object.hasOwn(value, "b")
          ) {
            // Is a rgb or rgba color variable
            flatVarsStructure[structuredVariableName] = {
              id: varId,
              varType: resolvedType,
              modeName: modes.length > 1 ? modeName : undefined,
              value,
            };
          } else if (
            typeof value === "object" &&
            value !== null &&
            Object.hasOwn(value, "type") &&
            Object.hasOwn(value, "id")
          ) {
            const val = value as FigmaPluginApi.VariableAlias;
            if (val.type !== "VARIABLE_ALIAS") {
              return;
            }

            // Is an aliased variable
            flatVarsStructure[structuredVariableName] = {
              id: varId,
              varType: resolvedType,
              modeName: modes.length > 1 ? modeName : undefined,
              value: val,
            };
          }
        });
      });
    });
  }

  return flatVarsStructure;
};

const getVarsAndAliasesModeVariants = (
  varEntries: [string, ZeytalTokens.FlatVar][],
  currentVarData: ZeytalTokens.FlatVar,
  parentDepthLevel = 0,
): {
  modeName: string;
  value: FigmaPluginApi.VariableValue;
}[] => {
  const currentDepthLevel = parentDepthLevel + 1;
  const varValue = currentVarData.value as FigmaPluginApi.VariableAlias;
  const currentVarModeName = currentVarData.modeName
    ? `-${toKebabCase(currentVarData.modeName)}`
    : "";

  if (
    typeof varValue === "object" &&
    varValue !== null &&
    Object.hasOwn(varValue, "type") &&
    varValue.type === "VARIABLE_ALIAS" &&
    !!varValue.id
  ) {
    const aliases = varEntries.filter(([, { id }]) => id === varValue.id);

    return aliases.flatMap(([, aliasData]) => {
      const aliasedModesAndVals = getVarsAndAliasesModeVariants(
        varEntries,
        aliasData,
        currentDepthLevel,
      ).map(({ modeName: chainedModes, value }) => {
        const modeToAdd = currentDepthLevel > 1 ? currentVarModeName : "";

        return {
          modeName: modeToAdd + chainedModes,
          value,
        };
      });

      return aliasedModesAndVals;
    });
  } else {
    return [
      {
        modeName: currentVarModeName,
        value: currentVarData.value,
      },
    ];
  }
};

const modeAndAliasVariablesNames = (
  flatVarsStructure: ZeytalTokens.FlatVarsStructure,
) => {
  const varEntries = Object.entries(flatVarsStructure);
  const aliasedStructure: ZeytalTokens.FlatVarsStructure = {};

  varEntries.forEach(([currentVarName, currentVarData]) => {
    const varValue = currentVarData.value as FigmaPluginApi.VariableAlias;

    if (
      typeof varValue === "object" &&
      varValue !== null &&
      Object.hasOwn(varValue, "type") &&
      varValue.type === "VARIABLE_ALIAS" &&
      !!varValue.id
    ) {
      const aliases = getVarsAndAliasesModeVariants(varEntries, currentVarData);

      aliases.forEach((alias) => {
        aliasedStructure[currentVarName + alias.modeName] = {
          ...currentVarData,
          value: alias.value,
        };
      });
    } else {
      aliasedStructure[currentVarName] = currentVarData;
    }
  });

  return aliasedStructure;
};

const sortVariables = (flatVarsStructure: ZeytalTokens.FlatVarsStructure) => {
  const varEntries = Object.entries(flatVarsStructure);

  // Create a dependency graph to track which variables reference others
  const dependencyGraph: Record<string, string[]> = {};

  // Build the dependency graph
  varEntries.forEach(([varName, varData]) => {
    const { value } = varData;
    if (typeof value === "string" && value.startsWith(variableAliasKeyword)) {
      const referencedVar = value.replace(variableAliasKeyword, "");

      // Initialize arrays if they don't exist
      if (!dependencyGraph[varName]) {
        dependencyGraph[varName] = [];
      }

      // IMPORTANT: The variable depends on the referenced variable
      // not the other way around
      dependencyGraph[varName].push(referencedVar);
    }
  });

  // Topological sort
  const visited = new Set<string>();
  const temp = new Set<string>(); // For cycle detection
  const result: ZeytalTokens.ProcessedVarsArray = [];

  function visit(name: string) {
    // Skip if already visited
    if (visited.has(name)) return;
    // Check for cycles (not strictly necessary as you mentioned)
    if (temp.has(name)) return;

    temp.add(name);

    // Visit all dependencies first
    const dependencies = dependencyGraph[name] || [];
    for (const dependency of dependencies) {
      visit(dependency);
    }

    temp.delete(name);
    visited.add(name);

    // Add to result
    if (flatVarsStructure[name]) {
      result.push({ ...flatVarsStructure[name], name });
    }
  }

  // Visit all nodes
  Object.keys(flatVarsStructure).forEach((name) => {
    if (!visited.has(name)) {
      visit(name);
    }
  });

  // The result is in reverse topological order (dependencies first)
  // This is what we want - variables come before the variables that reference them
  return result;
};

function verifySorting(sortedVars: ZeytalTokens.ProcessedVarsArray): boolean {
  const varPositions: Record<string, number> = {};

  // Record the position of each variable
  sortedVars.forEach((varData, index) => {
    varPositions[varData.name] = index;
  });

  // Check that each alias comes after its referenced variable
  for (let i = 0; i < sortedVars.length; i++) {
    const varData = sortedVars[i];

    if (!varData) continue;

    if (
      typeof varData.value === "string" &&
      varData.value.startsWith(variableAliasKeyword)
    ) {
      const referencedVar = varData.value.replace(variableAliasKeyword, "");
      if (referencedVar in varPositions) {
        if (!varPositions[referencedVar]) continue;

        if (varPositions[referencedVar] > i) {
          console.warn(
            `Sorting error: ${varData.name} at position ${i} references ${referencedVar} at position ${varPositions[referencedVar]}`,
          );
          return false;
        }
      }
    }
  }

  return true;
}

export const makeVariablesStructure = (
  figmaVariablesExportsStrings: string[],
  userConfig: ZeytalCLIPureConfig,
) => {
  const flatVarsStructure = flattenVariables(
    figmaVariablesExportsStrings,
    userConfig,
  );

  const modedAndAliasedVarsNamesStructure =
    modeAndAliasVariablesNames(flatVarsStructure);

  const sortedVars = sortVariables(modedAndAliasedVarsNamesStructure);

  verifySorting(sortedVars);

  return sortedVars;
};
